import { createContext } from "react";

/**
 * FocusContext.tsx
 *
 * This react context can be used to keep track of DOM elements
 * across components in the app
 *
 * For example, to trigger a "focus" event on the code pane editor from
 * other locations
 *
 * Avoid calling this directly by abstracting it into a hook like
 * ```
 * cost {setCodePaneEditorElement} = useCodePaneEditorElement()
 * ```
 */

/**
 * Types
 */
// define action names
export const UPDATE_CODE_PANE_EDITOR = "UPDATE_CODE_PANE_EDITOR";

// list all possible actions
const actions = [UPDATE_CODE_PANE_EDITOR];

export type FocusContextState = {
	codePaneEditorElement: HTMLElement | { focus: () => void } | null;
};

type Action = { type: (typeof actions)[number]; payload: unknown };

/**
 * State and Dispatch
 */
export const FocusContextInitialState: FocusContextState = {
	codePaneEditorElement: null,
};

export const FocusContextReducer: React.Reducer<FocusContextState, Action> = (state, action) => {
	switch (action.type) {
		case UPDATE_CODE_PANE_EDITOR: {
			return {
				...state,
				codePaneEditorElement: action.payload as HTMLElement,
			};
		}
		default: {
			return state;
		}
	}
};

/**
 * FocusContext
 */
export const FocusContext = createContext<{
	state: FocusContextState;
	dispatch: React.Dispatch<Action> | (() => void);
}>({ state: FocusContextInitialState, dispatch: () => undefined });
